import Cookies from 'js-cookie';
import { emitTokenRefreshed, emitLoggedOut } from './tokenEvents';

const BASEAPI = process.env.REACT_APP_BASE_API;

/** Refresh the access token this many ms before it actually expires. */
export const EXPIRY_SKEW_MS = 60 * 1000;

const isHttps = () => {
  try {
    return window.location.protocol === 'https:';
  } catch (e) {
    return true;
  }
};

export const cookieOptions = () => ({
  sameSite: 'Strict',
  secure: isHttps(),
  path: '/',
});

// ── JWT helpers ──────────────────────────────────────────────────────

const parseJwt = (token) => {
  if (!token || typeof token !== 'string') return null;
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

const isTokenExpired = (token) => {
  const decoded = parseJwt(token);
  if (!decoded || !decoded.exp) return true;
  return Date.now() >= decoded.exp * 1000;
};

// ── Token accessors ──────────────────────────────────────────────────

export const getToken = () => Cookies.get('token') || null;

export const getRefreshToken = () => Cookies.get('refreshToken') || null;

/** Public id (UUID) of the logged user, taken from the access token. */
export const getUserId = () => Cookies.get('user_public_id') || null;

/**
 * True when the access token expires within `skewMs`.
 * Used to refresh proactively instead of waiting for a 401.
 */
export const isAccessTokenExpiring = (skewMs = EXPIRY_SKEW_MS) => {
  const decoded = parseJwt(getToken());
  if (!decoded || typeof decoded.exp !== 'number') return false;
  return decoded.exp * 1000 - Date.now() <= skewMs;
};

// ── Session state ────────────────────────────────────────────────────

/**
 * The session is alive while we have either a valid access token or a
 * refresh token that can obtain a new one.
 *
 * IMPORTANT: never destroy the refresh token just because the (15 minute)
 * access token expired — that would log the user out mid-session.
 */
export const isLogged = () => {
  const token = getToken();
  const refreshToken = getRefreshToken();

  if (!token && !refreshToken) return false;

  if (token && !isTokenExpired(token)) return true;

  // Access token missing/expired but we can still refresh the session.
  if (refreshToken) return true;

  // Nothing usable left — clean up.
  doLogout({ revoke: false });
  return false;
};

export const isAdmin = () => {
  const decoded = parseJwt(getToken());
  if (!decoded) return false;

  // New tokens carry a `role`; keep supporting the legacy `is_admin` flag.
  const role = decoded.role ?? decoded.user_role ?? decoded.userType;
  if (typeof role === 'string' && role.toLowerCase() === 'admin') return true;

  return (
    decoded.is_admin === 1 ||
    decoded.is_admin === '1' ||
    decoded.is_admin === true ||
    decoded.isAdmin === true
  );
};

export const getUserData = () => {
  const token = getToken();
  if (!token) return null;
  return parseJwt(token);
};

// ── Login / logout ───────────────────────────────────────────────────

export const doLogin = (token, rememberLogin = false, refreshToken = null) => {
  const baseOptions = cookieOptions();
  const persistent = rememberLogin ? { ...baseOptions, expires: 999 } : baseOptions;

  Cookies.set('token', token, persistent);

  if (refreshToken) {
    Cookies.set(
      'refreshToken',
      refreshToken,
      rememberLogin ? { ...baseOptions, expires: 7 } : baseOptions
    );
  }

  // Store user_public_id for ownership checks (JWT may use `sub` or `id`)
  const decoded = parseJwt(token);
  const userId = decoded?.sub || decoded?.id;
  if (userId) {
    Cookies.set('user_public_id', userId, persistent);
  }

  // Let the socket layer pick up the fresh token
  emitTokenRefreshed(token);
};

/**
 * Ends the session locally and (best effort) revokes the refresh token on the
 * server. Returns a promise, but callers may fire-and-forget.
 */
export const doLogout = ({ revoke = true } = {}) => {
  const token = getToken();
  const refreshToken = getRefreshToken();

  Cookies.remove('token', { path: '/' });
  Cookies.remove('refreshToken', { path: '/' });
  Cookies.remove('user_public_id', { path: '/' });

  emitLoggedOut();

  if (revoke && BASEAPI) {
    // Fire-and-forget: the UI must not wait for the network to log out.
    try {
      fetch(`${BASEAPI}/user/signout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Platform': 'web',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(refreshToken ? { refreshToken } : {}),
      }).catch(() => {});
    } catch (e) {
      // ignore network/JSON errors
    }
  }

  return Promise.resolve();
};
